import { StateService } from "@uirouter/angularjs";
import { IOnInit, IPromise, IScope } from "angular";
import {
  IExpressionFunction,
  IFieldArray,
  ITemplateScope,
} from "AngularFormly";
import { cloneDeep } from "lodash";
import { RecordFileTemplateOptions } from "../../../../../../app/records/forms/formly/types";
import { FormViewController } from "../../../../../../app/records/forms/formView/controller";

import {
  FormViewService,
  vsName as FormViewServiceName,
} from "../../../../../../app/records/forms/formView/service";
import fieldHelpers from "../../../../../../app/ui-formly/fieldHelpers";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../../records/forms/formly/fieldConfigurationHelpers";
import { File } from "../../../../../records/server/crud";
import {
  RecordsUtilsService,
  vsName as RecordsUtilsServiceName,
} from "../../../../../records/utils";
import { MachineConfigurationViewGateway } from "../../../models/machineConfiguration";

interface IMultipleMoveFormModel {
  MachineChanges: IMachineMoveRequest[];
  Date?: Date;
  Files: File[];
  Notes?: string;
}

interface IMachineMoveRequest {
  PropertyId?: number;
  PropertyName?: string;
  MachineId?: number;
  MachineBankId?: number;
  MachineBankName?: string;
  Position?: string;
  MachineAreaId?: number;
  MachineAreaName?: string;
  MachineZoneId?: number;
  MachineZoneName?: string;
}

export class MachinesMoveViewController implements IOnInit {
  static $inject = [
    FormViewServiceName,
    "$state",
    FieldConfigurationHelperServiceName,
    "$scope",
    RecordsUtilsServiceName,
  ];

  formFields!: IFieldArray;

  /* start bindings */
  machineConfigurations!: MachineConfigurationViewGateway[];
  /* end binding */

  model!: IMultipleMoveFormModel;
  machineInfo!: { machineConfigurations: MachineConfigurationViewGateway[] };

  constructor(
    private readonly formViewService: FormViewService,
    private readonly $state: StateService,
    private readonly fieldConfigurationHelperService: FieldConfigurationHelperService,
    private readonly $scope: IScope,
    private readonly recordUtils: RecordsUtilsService,
  ) {
    $scope.$on("copyDown", (e, modelClicked) => {
      e.preventDefault();
      this.copyDown(modelClicked);
    });
  }

  private getPrevious(propertyName: string): IExpressionFunction {
    return (_: any, __: any, scope: ITemplateScope): string | undefined => {
      const machineConfigurationView =
        this.getMachineConfigurationViewFromMachineId(scope.model?.MachineId);
      const value = machineConfigurationView?.[propertyName];
      return value ? `Previous: ${value}` : undefined;
    };
  }

  private initializeFormFields() {
    const fileTemplateOptions: RecordFileTemplateOptions = {
      allowMultiple: true,
      label: "Files",
    };

    this.formFields = [
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleDateField({
            key: "Date",
            name: "Date",
            label: "Project Date",
            required: true,
          }),
          {
            key: "ProjectNumber",
            name: "ProjectNumber",
            type: "input",
            templateOptions: {
              label: "Project Number",
              required: true,
            },
          },
        ],
        4,
      ),
      this.fieldConfigurationHelperService.repeaterField({
        key: "MachineChanges",
        name: "MachineChanges",
        allowAdd: false,
        allowAddBottom: false,
        displayNameSingular: "Machine",
        repeaterFields: fieldHelpers.columnLayout(
          [
            fieldHelpers.simpleReadonlyField({
              key: "MachineId",
              label: "Serial Number",
              formatters: [
                (machineId) => {
                  const machineConfigurationView =
                    this.getMachineConfigurationViewFromMachineId(
                      <number>machineId,
                    );
                  return machineConfigurationView?.SerialNumber;
                },
              ],
            }),
            fieldHelpers.simpleStringField({
              label: "Asset Number",
              key: "SlotNumber",
              required: true,
              expressionProperties: {
                "templateOptions.helpText": this.getPrevious("SlotNumber"),
              },
            }),
            this.fieldConfigurationHelperService.selectAsyncField({
              key: "PropertyId",
              name: "PropertyId",
              label: "Property",
              required: true,
              getDefaultItem: (model: IMachineMoveRequest) => {
                return {
                  Id: model.PropertyId ?? 0,
                  Name: model.PropertyName ?? "",
                };
              },
              onSelect: (selectedItem: any, model: any) => {
                model.PropertyName = selectedItem?.Name;
              },
              queryUrl: "/api/properties",
              expressionProperties: {
                "templateOptions.helpText": this.getPrevious("PropertyName"),
              },
            }),
            this.fieldConfigurationHelperService.selectAsyncField({
              key: "MachineAreaId",
              name: "MachineAreaId",
              label: "Area",
              required: true,
              getDefaultItem: (model: IMachineMoveRequest) => {
                return {
                  Id: model.MachineAreaId ?? 0,
                  Name: model.MachineAreaName ?? "",
                };
              },
              onSelect: (selectedItem: any, model: any) => {
                model.MachineAreaName = selectedItem?.Name;
              },
              disableDefaultQueryCaching: true,
              queryUrl: "/api/machines/MachineArea",
              expressionProperties: {
                "templateOptions.helpText": this.getPrevious("MachineAreaName"),
              },
              queryBuilder: (search, model) => {
                const propertyId = model.PropertyId;
                return {
                  NameContains: search,
                  PropertyId: propertyId,
                };
              },
              watcher: {
                expression: (_, scope) => {
                  return scope.model?.PropertyId;
                },
                listener: (_, newValue, oldValue, scope) => {
                  if (newValue !== oldValue && scope.model) {
                    scope.model.MachineAreaId = undefined;
                    scope.model.MachineAreaName = undefined;
                  }
                },
              },
            }),
            this.fieldConfigurationHelperService.selectAsyncField({
              key: "MachineZoneId",
              name: "MachineZoneId",
              label: "Zone",
              required: true,
              getDefaultItem: (model: IMachineMoveRequest) => {
                return {
                  Id: model.MachineZoneId ?? 0,
                  Name: model.MachineZoneName ?? "",
                };
              },
              onSelect: (selectedItem: any, model: any) => {
                model.MachineZoneName = selectedItem?.Name;
              },
              disableDefaultQueryCaching: true,
              queryUrl: "/api/machines/MachineZone",
              expressionProperties: {
                "templateOptions.helpText": this.getPrevious("MachineZoneName"),
              },
              queryBuilder: (search, model) => {
                const machineAreaId = model.MachineAreaId ?? 0;
                return {
                  NameContains: search,
                  MachineAreaId: machineAreaId,
                };
              },
              watcher: {
                expression: (_, scope) => scope.model?.MachineAreaId,
                listener: (_, newValue, oldValue, scope) => {
                  if (newValue !== oldValue && scope.model) {
                    scope.model.MachineZoneId = undefined;
                    scope.model.MachineZoneName = undefined;
                  }
                },
              },
            }),
            this.fieldConfigurationHelperService.selectAsyncField({
              key: "MachineBankId",
              name: "MachineBankId",
              label: "Bank",
              required: true,
              getDefaultItem: (model: IMachineMoveRequest) => {
                return {
                  Id: model.MachineBankId ?? 0,
                  Name: model.MachineBankName ?? "",
                };
              },
              onSelect: (selectedItem: any, model: any) => {
                model.MachineBankName = selectedItem?.Name;
              },
              expressionProperties: {
                "templateOptions.helpText": this.getPrevious("MachineBankName"),
              },
              queryBuilder: (search, model) => {
                const machineZoneId = model.MachineZoneId ?? 0;
                return {
                  NameContains: search,
                  MachineZoneId: machineZoneId,
                };
              },
              watcher: {
                expression: (_, scope) => scope.model?.MachineZoneId,
                listener: (_, newValue, oldValue, scope) => {
                  if (newValue !== oldValue && scope.model) {
                    scope.model.MachineBankId = undefined;
                    scope.model.MachineBankName = undefined;
                  }
                },
              },
              queryUrl: "/api/machines/MachineBank",
              disableDefaultQueryCaching: true,
            }),
            {
              key: "Position",
              name: "Position",
              type: "input",
              templateOptions: {
                label: "Position",
                required: true,
                minlength: 2,
                maxlength: 2,
              },
              expressionProperties: {
                "templateOptions.helpText": this.getPrevious("Position"),
              },
            },
            {
              key: "copyDown",
              name: "copyDown",
              templateOptions: {
                label: " ",
              },
              template: `
            <div>
              <div class="pull-right">
                <div class="btn-group">
                  <div class="btn btn-primary game-config-btn" ng-click="$emit('copyDown', model)">
                    <i class="glyphicon glyphicon-arrow-down"></i>
                  </div>
                </div>
              </div>
            </div>
            `,
              wrapper: ["bootstrapLabel", "bootstrapHasError"],
            },
          ],
          4,
        ),
        showHeader: true,
      }),
      {
        key: "Files",
        name: "Files",
        type: "recordFileUpload",
        templateOptions: fileTemplateOptions,
      },
      fieldHelpers.headerField({ headerText: "Notes", key: "NotesHeader" }),
      fieldHelpers.textAreaField({
        key: "Notes",
        name: "Notes",
        label: " ",
        rows: 5,
      }),
    ];
  }

  $onInit(): void {
    this.model = {
      Files: [],
      MachineChanges: this.machineConfigurations.map((mc) => {
        return {
          MachineId: mc.MachineId,
          PropertyId: mc.PropertyId,
          PropertyName: mc.PropertyName,
          SlotNumber: mc.SlotNumber,
        };
      }),
    };
    this.machineInfo = {
      machineConfigurations: cloneDeep(this.machineConfigurations),
    };
    this.initializeFormFields();
  }

  //TODO find index of passed in model and then update all rows after that index with the same MachineBankId
  copyDown(modelClicked: IMachineMoveRequest) {
    const machineMoves = this.model.MachineChanges;
    const copyIndex = machineMoves.indexOf(modelClicked);
    for (let i = copyIndex + 1; i < machineMoves.length; i++) {
      const newModel: IMachineMoveRequest = {
        MachineAreaId: modelClicked.MachineAreaId,
        MachineAreaName: modelClicked.MachineAreaName,
        MachineBankId: modelClicked.MachineBankId,
        MachineBankName: modelClicked.MachineBankName,
        MachineId: machineMoves[i].MachineId,
        Position: undefined,
        PropertyId: modelClicked.PropertyId,
        PropertyName: modelClicked.PropertyName,
        MachineZoneId: modelClicked.MachineZoneId,
        MachineZoneName: modelClicked.MachineZoneName,
      };
      this.recordUtils.assignId(newModel);
      machineMoves.splice(i, 1, newModel);
    }
  }

  submit(formViewController: FormViewController): IPromise<void> {
    return this.formViewService
      .submitForm({
        url: `/api/machines/move`,
        formObject: this.model,
        formViewController,
        method: "post",
      })
      .then(() => {
        this.$state.go("^.grid", undefined, { reload: true });
      });
  }

  async cancel(): Promise<void> {
    await this.$state.go("^.grid", undefined, { reload: true });
  }

  private getMachineConfigurationViewFromMachineId(machineId: number) {
    return this.machineInfo.machineConfigurations.find(
      (mc) => mc.MachineId === machineId,
    );
  }
}
